import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule, WindowModule } from '@progress/kendo-angular-dialog';
import { GridModule } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { PagerModule } from '@progress/kendo-angular-pager';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { UserDataService } from '../../users/user-data.service';
import { Subscription } from 'rxjs';
import { CasiDateRange, PlacementReviewStudentType, User } from '../../models/models.lib';
import { DateInputsModule, FormatSettings } from '@progress/kendo-angular-dateinputs';
import moment from 'moment';
import { EditorModule } from '@progress/kendo-angular-editor';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';

@Component({
  selector: 'casi-my-placement-review-add-as-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DialogModule,
    EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    LayoutModule,
    AutoFocusDirective,
    DateInputsModule,
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './my-placement-review-add-as-win.html',
  styleUrl: './my-placement-review-add-as-win.scss'
})
export class MyPlacementReviewAddAsWin implements OnInit, OnDestroy {

  private _myReview: PlacementReviewStudentType | undefined;

  @Input() title: string = 'Title Not Set';


  @Input({ required: true }) set myReview(val: PlacementReviewStudentType) {
    this._myReview = val;
    this.editForm.patchValue(this._myReview);
  }
  get myReview(): PlacementReviewStudentType | undefined {
    return this._myReview;
  };

  @Output() onCancel = new EventEmitter<void>();
  @Output() submitForm: EventEmitter<FormGroup> = new EventEmitter();

  editForm: FormGroup;
  supervisorDs: User[] = [];
  additionalSupervisorDs: User[] = [];
  users: User[] = [];
  public dateRange: CasiDateRange = new CasiDateRange(null, null);
  public format: FormatSettings = {
    displayFormat: "dd/MM/yyyy",
    inputFormat: "dd/MM/yy",
  };

  private _subs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private logger: LoggerService,
    private me: MeService,
    private dataService: UserDataService
  ) {
    this.editForm = this.fb.group(
      {
        id: [null, []],
        placement_review_id: [null, []],
        student_institution_id: [null, []],
        student_email: [null, []],
        placement_location: [null, [Validators.required]],
        placement_start_date: [null, []],
        placement_end_date: [null, []],
        supervisor_user_id: [{ value: null, disabled: true }],
        additional_supervisor_user_id: [{ value: null, disabled: true }],
        start: [null, [Validators.required]],
        end: [null, [Validators.required]],
        year_first_practice: [null, [Validators.min(1971), Validators.max(new Date().getFullYear()), Validators.pattern(/^[0-9]+$/)]]
      }
    );
  }

  //#region Ng Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');

    const { placement_start_date, placement_end_date } = this.editForm.value;
    this.dateRange = CasiDateRange.create(placement_start_date, placement_end_date);

    this.editForm.patchValue({
      start: placement_start_date ? new Date(placement_start_date) : null,
      end: placement_end_date ? new Date(placement_end_date) : null
    });

    this.loadUsers();

    this.editForm.get('supervisor_user_id')!.valueChanges.subscribe(id => {
      this.updateSupervisors(id, undefined);
    });

    this.editForm.get('additional_supervisor_user_id')!.valueChanges.subscribe(id => {
      this.updateSupervisors(undefined, id);
    });

    // this.editForm.get('supervisor_user_id')!.valueChanges.subscribe(id => {
    //   if (id == this.editForm.value.additional_supervisor_user_id)
    //     this.editForm.patchValue({ additional_supervisor_user_id: null });
    //
    //   this.additionalSupervisorDs = this.users.filter((u: User) => u.id !== id);
    // });
    //
    // this.editForm.get('additional_supervisor_user_id')!.valueChanges.subscribe(id => {
    //   if (id == this.editForm.value.supervisor_user_id)
    //     this.editForm.patchValue({ supervisor_user_id: null });
    //
    //   this.supervisorDs = this.users.filter((u: User) => u.id !== id);
    // });
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Form

  get f() { return this.editForm.controls; }
  get supervisor_user_id() { return this.editForm.get('supervisor_user_id'); }
  get additional_supervisor_user_id() { return this.editForm.get('additional_supervisor_user_id'); }
  get placement_location() { return this.editForm.get('placement_location'); }
  get start() { return this.editForm.get('start'); }
  get end() { return this.editForm.get('end'); }
  get year_first_practice() { return this.editForm.get('year_first_practice') }


  //#endregion

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onCancel.emit();
  }

  public onSaveAction(): void {
    this.logger.debug(`onSaveAction...`, this.editForm.value);
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const { start, end } = this.dateRange;
    this.editForm.patchValue({ placement_start_date: start ? moment(start).format("YYYY-MM-DD") : null });
    this.editForm.patchValue({ placement_end_date: start ? moment(end).format("YYYY-MM-DD") : null });

    this.submitForm.emit(this.editForm);
  }

  private updateSupervisors(supervisorIdChanged?: number, additionalSupervisorIdChanged?: number): void {
    const supervisorId = supervisorIdChanged || this.editForm.value.supervisor_user_id;
    const additionalSupervisorId = additionalSupervisorIdChanged || this.editForm.value.additional_supervisor_user_id;

    if (supervisorId === additionalSupervisorId && supervisorId != null)
      this.editForm.patchValue({ additional_supervisor_user_id: null }, { emitEvent: false });


    this.additionalSupervisorDs = this.users.filter(u => u.id !== supervisorId);
    this.supervisorDs = this.users.filter(u => u.id !== additionalSupervisorId);
  }

  private loadUsers(): void {
    this.dataService.get().subscribe({
      next: data => {
        this.users = data ? data.filter((d: User) => d.active && d.display_name) : [];
        this.supervisorDs = this.users;
        this.additionalSupervisorDs = this.users;
        this.updateSupervisors();
      },
      error: err => console.error('Failed to load users', err)
    });
  }

}
